import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { LoginWithMicrosoft, NotFound } from "@sal/portal";
import { lazy, Suspense } from "react";
import { HashRouter, Outlet, Routes, Route, useParams } from "react-router-dom";
import BasePage from "./pages/Layout/BasePage";
import { LoginPage } from "./pages/LoginPage";
import { LogoutPage } from "./pages/LogoutPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { routePermissionMapping } from "./appsettings";
import {
	PortalAuthenticatedRoute as _PortalAuthenticatedRoute,
	PortalAuthenticatedRouteProps
} from "@sal/onevent-portal";
import { useAuth } from "@sal/authentication.react";

const HomePage = lazy(() => import("./pages/HomePage"));
const ConsignmentDashboardPage = lazy(() => import("./pages/ConsignmentDashboardPage"));
const FreightBookingPage = lazy(() => import("./pages/FreightBookingPage"));
const FreightTrackerPage = lazy(() => import("./pages/FreightTrackerPage"));
const RatesPage = lazy(() => import("./pages/RatesPage"));
const ContainerTransportDashboardPage = lazy(() => import("./pages/ContainerTransportDashboardPage"));
const ContainerTransportImportFCLOrderPage = lazy(() => import("./pages/ContainerTransportImportFCLOrderPage"));
const ContainerTransportExportFCLOrderPage = lazy(() => import("./pages/ContainerTransportExportFCLOrderPage"));
const ContainerTransportImportContainerPage = lazy(() => import("./pages/ContainerTransportImportContainerPage"));
const ContainerTransportExportContainerPage = lazy(() => import("./pages/ContainerTransportExportContainerPage"));
const ContainerTransportMoveOrderPage = lazy(() => import("./pages/ContainerTransportMoveOrderPage"));
const ContainerTransportMoveContainerPage = lazy(() => import("./pages/ContainerTransportMoveContainerPage"));

const ContainerTransportOrderPage = lazy(() => import("./pages/ContainerTransportOrderPage"));

const ContainerConsignmentTrackerPage = lazy(() => import("./pages/ContainerConsignmentTrackerPage"));
const TrackPage = lazy(() => import("./pages/TrackPage"));
const ConsignmentListPage = lazy(() => import("./pages/ConsignmentListPage"));
const NetworkHealthPage = lazy(() => import("./pages/NetworkHealthPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const CustomersPage = lazy(() => import("./pages/CustomersPage"));
const LocationPage = lazy(() => import("./pages/LocationPage"));
const LocationDetailPage = lazy(() => import("./pages/LocationDetailPage"));
const LocationNewPage = lazy(() => import("./pages/LocationNewPage"));

const UserDashboardPage = lazy(() => import("./pages/UserDashboardPage"));
const UserDetailPage = lazy(() => import("./pages/UserDetailPage"));
const UserNewPage = lazy(() => import("./pages/UserNewPage"));
const MyAccountPage = lazy(() => import("./pages/MyAccountPage"));

function ContainerTransportOrderPageWithId() {
	const { id } = useParams();
	return <ContainerTransportOrderPage key={id} />;
}

function PortalAuthenticatedRoute(props: PortalAuthenticatedRouteProps) {
	const auth = useAuth();
	const loginPath = auth.authenticationMethod === "MicrosoftSSO" ?  "/SingleSignOn/Microsoft": "/Login";
	return <_PortalAuthenticatedRoute {...props} loginPath={loginPath} />;
}

const {
	dashboard,
	freightBooking,
	freightDetail,
	containerDetail,
	track,
	rates,
	consignmentList,
	networkHealth,
	users,
	analytics,
	customers,
	locations,
	containerTransportDashboard,
	importFCLTransportOrder,
	exportFCLTransportOrder,
	importContainer,
	exportContainer,
	moveOrder,
	moveContainer,
	order
} = routePermissionMapping;

export default function Router() {
	return (
		<HashRouter>
			<Routes>
				<Route element={<PortalAuthenticatedRoute />}>
					<Route element={<BasePage />}>
						<Route
							element={
								<Suspense
									fallback={
										<Backdrop invisible open>
											<CircularProgress />
										</Backdrop>
									}
								>
									<Outlet />
								</Suspense>
							}
						>
							<Route path="/" element={<HomePage />} />
							<Route
								path={dashboard.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={dashboard.securityContext}
										securityContextPermission={dashboard.securityContextPermission}
									>
										<ConsignmentDashboardPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={freightBooking.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={freightBooking.securityContext}
										securityContextPermission={freightBooking.securityContextPermission}
									>
										<FreightBookingPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={freightBooking.path + "/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={freightBooking.securityContext}
										securityContextPermission={"Read"}
									>
										<FreightBookingPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={freightDetail.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={freightDetail.securityContext}
										securityContextPermission={freightDetail.securityContextPermission}
									>
										<FreightTrackerPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={containerDetail.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={containerDetail.securityContext}
										securityContextPermission={containerDetail.securityContextPermission}
									>
										<ContainerConsignmentTrackerPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={containerTransportDashboard.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={containerTransportDashboard.securityContext}
										securityContextPermission={
											containerTransportDashboard.securityContextPermission
										}
									>
										<ContainerTransportDashboardPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={importFCLTransportOrder.path + "/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={importFCLTransportOrder.securityContext}
										securityContextPermission={importFCLTransportOrder.securityContextPermission}
									>
										<ContainerTransportImportFCLOrderPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={exportFCLTransportOrder.path + "/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={exportFCLTransportOrder.securityContext}
										securityContextPermission={exportFCLTransportOrder.securityContextPermission}
									>
										<ContainerTransportExportFCLOrderPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={importContainer.path + "/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={importContainer.securityContext}
										securityContextPermission={importContainer.securityContextPermission}
									>
										<ContainerTransportImportContainerPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={exportContainer.path + "/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={exportContainer.securityContext}
										securityContextPermission={exportContainer.securityContextPermission}
									>
										<ContainerTransportExportContainerPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={moveOrder.path + "/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={moveOrder.securityContext}
										securityContextPermission={moveOrder.securityContextPermission}
									>
										<ContainerTransportMoveOrderPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={moveContainer.path + "/:id"}
								element={
									<PortalAuthenticatedRoute
										securityContext={moveContainer.securityContext}
										securityContextPermission={moveContainer.securityContextPermission}
									>
										<ContainerTransportMoveContainerPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={order.path}
								element={
									<PortalAuthenticatedRoute permissions={order.permissions || []}>
										<ContainerTransportOrderPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={order.path + "/:id"}
								element={
									<PortalAuthenticatedRoute permissions={order.permissions || []}>
										<ContainerTransportOrderPageWithId />
									</PortalAuthenticatedRoute>
								}
							/>

							<Route
								path={track.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={track.securityContext}
										securityContextPermission={track.securityContextPermission}
									>
										<TrackPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={rates.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={rates.securityContext}
										securityContextPermission={rates.securityContextPermission}
									>
										<RatesPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={consignmentList.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={consignmentList.securityContext}
										securityContextPermission={consignmentList.securityContextPermission}
									>
										<ConsignmentListPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={users.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={users.securityContext}
										securityContextPermission={users.securityContextPermission}
									/>
								}
							>
								<Route index element={<UserDashboardPage />} />
								<Route path=":id" element={<UserDetailPage />} />
								<Route
									path="new"
									element={
										<PortalAuthenticatedRoute
											securityContext={users.securityContext}
											securityContextPermission="Write"
										>
											<UserNewPage />
										</PortalAuthenticatedRoute>
									}
								/>
							</Route>
							<Route
								path={networkHealth.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={networkHealth.securityContext}
										securityContextPermission={networkHealth.securityContextPermission}
									>
										<NetworkHealthPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={analytics.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={analytics.securityContext}
										securityContextPermission={analytics.securityContextPermission}
									>
										<AnalyticsPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={customers.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={customers.securityContext}
										securityContextPermission={customers.securityContextPermission}
									>
										<CustomersPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={locations.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={locations.securityContext}
										securityContextPermission={locations.securityContextPermission}
									/>
								}
							>
								<Route index element={<LocationPage />} />
								<Route
									path=":id"
									element={
										<PortalAuthenticatedRoute
											securityContext={locations.securityContext}
											securityContextPermission="Web_Locations_Write"
										>
											<LocationDetailPage />
										</PortalAuthenticatedRoute>
									}
								/>
								<Route
									path="New"
									element={
										<PortalAuthenticatedRoute
											securityContext={locations.securityContext}
											securityContextPermission="Web_Locations_Write"
										>
											<LocationNewPage />
										</PortalAuthenticatedRoute>
									}
								/>
							</Route>
							{/* No permissions needed */}
							<Route path="/MyAccount" element={<MyAccountPage />} />
						</Route>
					</Route>
				</Route>
				{/* Portal Framework */}
				<Route path="/Logout" element={<LogoutPage />} />
				<Route path="/Login" element={<LoginPage />} />
				{/*<Route path="/SingleSignOn/Microsoft" element={<LoginWithMicrosoft />} />*/}
				<Route path="/ForgotPassword" element={<ForgotPasswordPage />} />
				<Route path="/ResetPassword/:passwordResetToken" element={<ResetPasswordPage />} />
				<Route path="/NotFound" element={<NotFound />} />
				<Route path="*" element={<NotFound />} />
				{/* Portal Framework */}
			</Routes>
		</HashRouter>
	);
}
